/* eslint-disable @typescript-eslint/no-explicit-any */
// Location: src/components/admin/analytics/SocialProfileGameAnalytics.tsx
// Purpose: Social Profile Game analytics dashboard showing profile performance and leaderboards
// Scope: Display profile game metrics, leaderboards, and gamification status

import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
	Trophy,
	TrendingUp,
	ArrowLeft,
	Users,
	Star,
	Target,
	Zap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SocialProfileAnalytics } from '@/components/gamification/SocialProfileAnalytics';
import { Leaderboard } from '@/components/gamification/Leaderboard';

interface SocialProfileGameAnalyticsProps {
	className?: string;
}

export function SocialProfileGameAnalytics({
	className,
}: SocialProfileGameAnalyticsProps) {
	const { user, isAdmin } = useAuth();
	const navigate = useNavigate();

	// Check if game is active
	const gameStatus = useQuery(api.gamificationSettings.getGameStatus);
	const isGameActive = gameStatus?.status === 'active';

	// Get game stats
	// TODO: Implement gamificationStats.getGameStatsSummary in backend
	const gameStats: any = null; // Temporarily disabled until backend function is implemented
	/* const gameStats = useQuery(
		api.gamificationStats.getGameStatsSummary,
		user ? { userId: user._id as unknown as Id<'users'> } : 'skip'
	); */

	if (!user || !isAdmin) {
		return (
			<div className='container mx-auto p-6'>
				<Card className='mt-6'>
					<CardContent>
						<div className='flex flex-col items-center justify-center space-y-6 p-8'>
							<h2 className='text-2xl font-bold text-red-500'>
								Access Denied
							</h2>
							<p className='text-lg text-gray-400 text-center'>
								You do not have permission to access Social
								Profile Game Analytics.
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	const formatNumber = (num: number | undefined) => {
		if (num === undefined || num === null) return '0';
		return num.toLocaleString();
	};

	return (
		<div className={cn('space-y-6 my-20', className)}>
			{/* Header with Back Button */}
			<div>
				<Button
					variant='ghost'
					size='sm'
					onClick={() => navigate('/admin/analytics')}
					className='hover:bg-yellow-500/10'
				>
					<ArrowLeft className='h-4 w-4 mr-2' />
					Back to Analytics
				</Button>
			</div>
			<div className='flex items-center justify-between'>
				<div className='flex items-center gap-4'>
					<div>
						<h1 className='text-3xl font-bold text-white flex items-center gap-3'>
							<Trophy className='h-8 w-8 text-blue-500' />
							Social Profile Game
						</h1>
						<p className='text-muted-foreground mt-2'>
							View profile game leaderboards and performance
							metrics
						</p>
					</div>
				</div>
				<div className='flex items-center gap-2'>
					<Badge
						variant={isGameActive ? 'default' : 'secondary'}
						className={cn(
							'px-4 py-2',
							isGameActive
								? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0'
								: 'bg-gray-500/20 text-gray-400'
						)}
					>
						{isGameActive ? (
							<>
								<Zap className='h-3 w-3 mr-1 animate-pulse' />
								Game Active
							</>
						) : (
							'Game Inactive'
						)}
					</Badge>
				</div>
			</div>

			{/* Game Stats Overview */}
			{gameStats && (
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4'>
					<Card className='bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-500/20'>
						<CardHeader className='flex flex-row items-center space-y-0 pb-2'>
							<CardTitle className='text-sm font-medium flex items-center gap-2'>
								<Users className='h-4 w-4 text-blue-500' />
								PLAYERS
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className='text-2xl font-bold text-blue-500'>
								{formatNumber(gameStats.totalPlayers)}
							</div>
							<p className='text-xs text-muted-foreground'>
								Active Players
							</p>
						</CardContent>
					</Card>

					<Card className='bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20'>
						<CardHeader className='flex flex-row items-center space-y-0 pb-2'>
							<CardTitle className='text-sm font-medium flex items-center gap-2'>
								<Trophy className='h-4 w-4 text-purple-500' />
								PROFILES
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className='text-2xl font-bold text-purple-500'>
								{formatNumber(gameStats.totalProfiles)}
							</div>
							<p className='text-xs text-muted-foreground'>
								Gamified Profiles
							</p>
						</CardContent>
					</Card>

					<Card className='bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20'>
						<CardHeader className='flex flex-row items-center space-y-0 pb-2'>
							<CardTitle className='text-sm font-medium flex items-center gap-2'>
								<Star className='h-4 w-4 text-yellow-500' />
								TOP SCORE
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className='text-2xl font-bold text-yellow-500'>
								{formatNumber(gameStats.topScore)}
							</div>
							<p className='text-xs text-muted-foreground'>
								Highest Score
							</p>
						</CardContent>
					</Card>

					<Card className='bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20'>
						<CardHeader className='flex flex-row items-center space-y-0 pb-2'>
							<CardTitle className='text-sm font-medium flex items-center gap-2'>
								<TrendingUp className='h-4 w-4 text-green-500' />
								AVG SCORE
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className='text-2xl font-bold text-green-500'>
								{formatNumber(gameStats.averageScore)}
							</div>
							<p className='text-xs text-muted-foreground'>
								Average Score
							</p>
						</CardContent>
					</Card>

					<Card className='bg-gradient-to-br from-pink-500/10 to-rose-500/10 border-pink-500/20'>
						<CardHeader className='flex flex-row items-center space-y-0 pb-2'>
							<CardTitle className='text-sm font-medium flex items-center gap-2'>
								<Target className='h-4 w-4 text-pink-500' />
								TRIBES
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className='text-2xl font-bold text-pink-500'>
								{formatNumber(gameStats.totalTribes)}
							</div>
							<p className='text-xs text-muted-foreground'>
								Active Tribes
							</p>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Social Profile Analytics - All Profiles */}
			<SocialProfileAnalytics
				userId={user._id as unknown as Id<'users'>}
			/>

			{/* Gamification Leaderboards */}
			{isGameActive && (
				<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
					{/* Profile Leaderboard */}
					<Card>
						<CardHeader>
							<CardTitle className='flex items-center gap-2'>
								<Trophy className='h-5 w-5 text-yellow-500' />
								Profile Champions
							</CardTitle>
						</CardHeader>
						<CardContent>
							<Leaderboard
								userId={user._id as unknown as Id<'users'>}
								defaultType='profile'
								defaultTimeframe='weekly'
								compact={true}
								limit={5}
							/>
						</CardContent>
					</Card>

					{/* Tribe Leaderboard */}
					<Card>
						<CardHeader>
							<CardTitle className='flex items-center gap-2'>
								<Trophy className='h-5 w-5 text-purple-500' />
								Tribe Champions
							</CardTitle>
						</CardHeader>
						<CardContent>
							<Leaderboard
								userId={user._id as unknown as Id<'users'>}
								defaultType='tribe'
								defaultTimeframe='weekly'
								compact={true}
								limit={5}
							/>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Full Leaderboard */}
			{isGameActive && (
				<Leaderboard
					userId={user._id as unknown as Id<'users'>}
					defaultType='profile'
					defaultTimeframe='weekly'
				/>
			)}

			{/* Info Card */}
			<Card className='bg-gradient-to-br from-blue-500/5 to-indigo-500/5 border-blue-500/20'>
				<CardContent className='p-6'>
					<div className='flex items-start gap-4'>
						<div className='p-3 bg-blue-500/10 rounded-xl'>
							<Trophy className='h-6 w-6 text-blue-500' />
						</div>
						<div className='flex-1'>
							<h3 className='font-semibold mb-2'>
								About Social Profile Game
							</h3>
							<p className='text-sm text-muted-foreground mb-3'>
								The Social Profile Game rewards users for
								growing their social media presence. Players
								earn points based on follower growth, engagement
								rates, and content performance across connected
								platforms. Compete individually or join tribes
								for team competitions.
							</p>
							<div className='flex gap-4 text-sm'>
								<div>
									<span className='text-muted-foreground'>
										Season:
									</span>
									<span className='ml-2 font-medium'>
										Season 1
									</span>
								</div>
								<div>
									<span className='text-muted-foreground'>
										Rewards:
									</span>
									<span className='ml-2 font-medium text-blue-500'>
										1000 KARMA Pool
									</span>
								</div>
								<div>
									<span className='text-muted-foreground'>
										Ends:
									</span>
									<span className='ml-2 font-medium'>
										14 days
									</span>
								</div>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
